import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { CardElement, ElementsConsumer } from '@stripe/react-stripe-js'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { PaymentConstants } from '../../constants/Payments'
import { ProductCodes } from '../../constants'
import { isOrganizationPlan } from '../../utils/Helper'

const emptyFormData = {
  email: '',
  password: '',
  passwordTwo: '',
  name: '',
  organization: '',
  noOfMembers: '',
  clientName: '',
}
const emptyFormErrors = {
  email: false,
  password: false,
  passwordTwo: false,
  name: false,
  organization: false,
  noOfMembers: false,
}

const RegisterUserForm = ({
  activeCycle,
  currentPlan,
  setRegisterFormData,
  changeActive,
  setBillingMemberCount,
}) => {
  const cardRef = useRef()
  const {
    errors,
    auth: { signUpLoading },
  } = useSelector((state) => state)
  const [formData, setFormData] = useState(emptyFormData)
  const [formErrors, setFormErrors] = useState(emptyFormErrors)
  const [cardToken, setCardToken] = useState('')
  const [cardVerificationHappened, setCardVerificationHappened] =
    useState(false)

  const isPlanOrg = currentPlan && isOrganizationPlan(currentPlan)

  const changeElement = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
    if (e.target.value) {
      setFormErrors({
        ...formErrors,
        [e.target.name]: false,
      })
    } else {
      setFormErrors({
        ...formErrors,
        [e.target.name]: true,
      })
    }
    if (e.target.name === 'passwordTwo') {
      if (e.target.value !== formData.password) {
        setFormErrors({
          ...formErrors,
          passwordTwo: true,
        })
      }
    }

    if (e.target.name === 'noOfMembers') {
      setBillingMemberCount(e.target.value)
    }
  }

  const signUp = (e) => {
    e.preventDefault()
    if (!validateForm()) {
      return
    }

    sessionStorage.removeItem('newUser')

    const newUser = {
      name: formData.name,
      email: formData.email,
      password: formData.password,
      password2: formData.password,
      organization: formData.organization,
      noOfMembers: formData.noOfMembers,
      signupIp: formData.signupIp,
      signupCountry: formData.signupCountry,
      cardToken,
      duration:
        activeCycle === 1
          ? PaymentConstants.subscriptionDuration.annually
          : PaymentConstants.subscriptionDuration.monthly,
      planId: currentPlan._id,
      price:
        activeCycle === 1
          ? currentPlan?.stripePriceAnnually
          : currentPlan?.stripePriceMonthly,
      isClient: !!formData.clientName,
      referralToken: formData.referralToken,
    }
    setRegisterFormData(newUser)
    changeActive(3)
  }

  const validateForm = () => {
    const data = formData
    let validated = true
    const newErrors = { ...formErrors }
    Object.keys(data).forEach((key) => {
      if (!isPlanOrg && (key === 'noOfMembers' || key === 'organization'))
        return validated

      if (!data[key]) {
        validated = false
        newErrors[key] = true
      }
      if (key === 'passwordTwo') {
        if (data[key] !== data.password) {
          validated = false
          newErrors[key] = true
        }
      }
    })
    if (!cardToken) {
      newErrors.cardElement = true
      validated = false
    }
    setFormErrors(newErrors)
    return validated
  }
  const handleSubmit = async (event, stripe, elements) => {
    event.preventDefault()

    if (!stripe || !elements) {
      return
    }

    const card = elements.getElement(CardElement)
    const result = await stripe.createToken(card)
    setCardVerificationHappened(true)

    if (result.error) {
      setFormErrors({
        ...formErrors,
        cardElement: true,
      })
    } else {
      setCardToken(result.token.id)
    }
  }
  const stripeElementChange = (el) => {
    if (cardVerificationHappened) {
      setCardVerificationHappened(false)
      setCardToken('')
    }
    setFormErrors({
      ...formErrors,
      cardElement: !el.complete,
    })

    if (el.complete) {
      cardRef.current.requestSubmit()
    }
  }

  useEffect(() => {
    // Get user session storage
    const newUser = sessionStorage.getItem('newUser')
    if (newUser) {
      const user = JSON.parse(newUser)
      setFormData({
        ...formData,
        email: user?.email,
        name: user?.name,
        password: user?.password,
        passwordTwo: user?.password2,
        signupIp: user?.signupIp,
        signupCountry: user?.signupCountry,
        organization: user?.clientName,
        clientName: user?.clientName,
        referralToken: user?.referralToken,
      })
    }
  }, [])

  return (
    <div className="register-new-user tikbox-column tikbox-form signup">
      <p className="tikbox-title signup">
        {currentPlan?.code === ProductCodes.Free
          ? 'You will not be charged for this free plan'
          : 'Absolutely free for 7 days. You will not be charged until after your 7 day trial. You can cancel at anytime.'}
      </p>
      <p className="tikbox-title signup">
        We only ask for card details to stop spam on our application.
      </p>

      <ElementsConsumer>
        {({ stripe, elements }) => (
          <form
            ref={cardRef}
            onSubmit={(event) => handleSubmit(event, stripe, elements)}
            style={{ marginBottom: 5 }}>
            {cardVerificationHappened && (
              <span
                style={{
                  fontSize: 10,
                  color: cardToken ? 'green' : 'red',
                  fontWeight: 'bold',
                }}>
                Card {cardToken ? 'verified' : 'failed'}
              </span>
            )}
            <CardElement
              className="stripe__card-element"
              id="card-element"
              options={{
                hidePostalCode: true,
                style: {
                  base: {
                    fontSize: '13px',
                    fontFamily: 'sans-serif',
                  },
                },
              }}
              onChange={(el) => stripeElementChange(el, 'card_number')}
            />{' '}
            {formErrors.cardElement && (
              <label className="error">Provide a valid card number</label>
            )}
          </form>
        )}
      </ElementsConsumer>

      <form onSubmit={signUp} autoComplete="off">
        {isPlanOrg && (
          <>
            <div className="tikbox-row-1">
              <div className="tikbox-input">
                <input
                  type="text"
                  name="organization"
                  className="form-control"
                  placeholder="Your Organization"
                  onChange={changeElement}
                  value={formData.organization}
                />
                {formErrors.organization && (
                  <label className="error">Organization is required</label>
                )}
              </div>
            </div>
            <div className="tikbox-row-1">
              <div className="tikbox-input">
                <input
                  min={1}
                  type="number"
                  name="noOfMembers"
                  placeholder="No of members"
                  className="form-control"
                  onChange={changeElement}
                  value={formData.noOfMembers}
                />
                {formErrors.noOfMembers && (
                  <label className="error">No of members is required</label>
                )}
              </div>
            </div>
          </>
        )}
        <div className="tikbox-row-1">
          <div className="tikbox-input">
            <input
              type="text"
              name="name"
              className="form-control"
              placeholder="Full Name"
              onChange={changeElement}
              value={formData.name}
            />
            {formErrors.name && (
              <label className="error">Name is required</label>
            )}
          </div>
        </div>
        <div className="tikbox-row-1">
          <div className="tikbox-input">
            <input
              type="email"
              name="email"
              className="form-control"
              placeholder="Your Email"
              onChange={changeElement}
              value={formData.email}
            />
            {formErrors.email && (
              <label className="error">Email is required</label>
            )}
          </div>
        </div>
        <div className="tikbox-row-1">
          <div className="tikbox-input">
            <input
              type="password"
              name="password"
              className="form-control"
              placeholder="Your Password"
              onChange={changeElement}
              value={formData.password}
            />
            {formErrors.password && (
              <label className="error">Password is required</label>
            )}
          </div>
        </div>
        <div className="tikbox-row-1">
          <div className="tikbox-input">
            <input
              type="password"
              name="passwordTwo"
              className="form-control"
              placeholder="Confirm your password"
              onChange={changeElement}
              value={formData.passwordTwo}
            />
            {formErrors.passwordTwo && (
              <label className="error">Password does not match</label>
            )}
          </div>
        </div>
        <p className="terms">
          By signing-up to an account you are agreeing to our{' '}
          <a
            href="https://web.tikbox.com/terms-conditions/"
            target="_blank"
            rel="noreferrer">
            terms and conditions
          </a>{' '}
          and{' '}
          <a
            href="https://web.tikbox.com/privacy-policy/"
            target="_blank"
            rel="noreferrer">
            privacy policy
          </a>
        </p>
        <button className="pay-button" type="submit" disabled={signUpLoading}>
          {'Subscribe'}
          {signUpLoading && (
            <FontAwesomeIcon className="spinner" icon={faSpinner} />
          )}
        </button>
        {errors && (
          <p className="text-red-500 text-xs italic mt-5">
            {errors.name} {errors.email} {errors.password} {errors.password2}
          </p>
        )}
      </form>
    </div>
  )
}

export default RegisterUserForm
